/* eslint-disable prefer-promise-reject-errors */
import { useCallback, useContext } from 'react';
import _ from 'underscore';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useInstrumentLogger } from '@bingads-webui-campaign-react/linkedin-instrumentation';
import {
  readCampaign,
  readAdGroup,
  createOrUpdateAd,
  readAd,
  getConnectedLinkedInCompanyPage,
  getDefaultAd,
  genericApiError,
  mapBulkUpsertErrors,
  getBulkUploadErrorType,
  ERROR_ENTITY,
  useLinkedInAdAdapter
} from '@bingads-webui-campaign-react/linkedin-data';
import { Campaign, AdGroup, ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-fluent-component/wizard';

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
  [getBulkUploadErrorType(ResponsiveAd)]: ERROR_ENTITY.Ad,
};

export const useAdEditorRepository = () => {
  const i18n = useContext(I18nContext);
  const { send } = useWizardService();
  const {
    scope: {
      values: {
        cid,
        aid,
        campaignId,
        adGroupId,
        adId,
      },
    },
  } = useContext(ScopeContext);

  const logger = useInstrumentLogger({
    scenarioName: 'LinkedInAdEditor',
    activityName: 'Repository',
  });

  const {
    mapToViewModel: mapLinkedInAdToViewModel,
    mapToBulkModel: mapLinkedInAdToBulkModel,
  } = useLinkedInAdAdapter();

  const loadCompanyPage = useCallback(() =>
    getConnectedLinkedInCompanyPage(cid, aid), [cid, aid]);

  const loadCampaign = useCallback(() =>
    readCampaign(cid, aid, campaignId), [cid, aid, campaignId]);

  const loadAdGroup = useCallback(() =>
    readAdGroup(cid, aid, campaignId, adGroupId), [cid, aid, campaignId, adGroupId]);

  const loadAd = useCallback(() => {
    if (!adId) {
      return Promise.resolve({});
    }

    return readAd(cid, aid, campaignId, adGroupId, adId).then(res => mapLinkedInAdToViewModel(res));
  }, [adId, aid, campaignId, cid, adGroupId, mapLinkedInAdToViewModel]);

  const load = useCallback(() => {
    const initialRequests = [
      loadCompanyPage(),
      loadCampaign(),
      loadAdGroup(),
      loadAd(),
    ];

    return Promise.all(initialRequests)
      .then(([companyInfo, campaign, adGroup, adData]) => {
        const isMemberTokenRevoked = companyInfo && companyInfo.IsMemberTokenActive === false;
        const finalAdDate = adData && adData.Id ? adData : getDefaultAd({ connectedCompanyPage: companyInfo });

        const data = {
          companyInfo,
          campaign,
          adGroup,
          adData: finalAdDate,
          isMemberTokenRevoked,
        };

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isMemberTokenRevoked });

        return data;
      })
      .catch((err) => {
        logger.error(`Failed to load initial ad data: ${JSON.stringify(err)}`);
        throw err;
      });
  }, [logger, loadAdGroup, loadCampaign, loadAd, loadCompanyPage, send]);

  const save = useCallback(({ data }) => {
    const { campaign, adGroup, adData } = data;

    const modelToSave = mapLinkedInAdToBulkModel(campaign, adGroup, adData);

    return createOrUpdateAd(modelToSave)
      .then(() => {
        logger.trace('Ad saved successfully');
        return data;
      })
      .catch((err) => {
        logger.error(`Failed to save ad:${JSON.stringify(err)}`);

        const errors = _.get(err, 'errors');
        const serverErrors = errors
          ? mapBulkUpsertErrors(errors, bulkUpsertEntityErrorMap, _.identity, null, i18n)
          : [genericApiError];

        return Promise.reject({ error: { serverErrors } });
      });
  }, [i18n, logger, mapLinkedInAdToBulkModel]);

  return {
    load,
    save,
  };
};
